import {StructuredRemarksView} from './StructuredRemarksView'
import React, {FC, useState, useEffect} from 'react'
import type {QHSEDefectDto} from '../core/_models'
import {FileViewerModal} from '../components/FileViewerModal'
import {
  defectAttachmentViewUrl,
  defectAttachmentDownloadUrl,
  defectClosureViewUrl,
  defectClosureDownloadUrl,
} from '../core/_requests'
import { getCompanyAdminList, getCompanyList, getVesselList } from '../../Management/core/_requests'
import { searchPmsJobs } from '../../Technical/core/pms/_requests'
import { CreateUnplannedJobModal } from '../../Technical/components/PMS/CreateUnplannedJobModal'
import { PmsJobDetailModal } from '../../Technical/components/PMS/PmsJobDetailModal'
import type { Vessel } from '../../Management/core/_models'
import type { PmsJobRecord } from '../../Technical/core/pms/_models'
import { toast } from 'react-toastify'
import {useNavigate} from 'react-router-dom'
import {linkedRequisitionPath} from '../core/requisitionNavigation'

interface Props {
  visible: boolean
  onClose: () => void
  record: QHSEDefectDto | null
  onOpenAttachments?: (record: QHSEDefectDto) => void
  origin?: 'job' | 'defect'
  wrapZIndex?: number
}

const BASE_WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const niceEnum = (s?: string | null) =>
  s ? s.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '-'

type CompanyGroup = { id: number; name: string }
type Subcompany = { id: number; name: string; companyId: number }

export const ViewDefectModal: FC<Props> = ({
  visible,
  onClose,
  record,
  onOpenAttachments,
  origin = 'defect',
  wrapZIndex,
}) => {
  const navigate = useNavigate()
  const [fileView, setFileView] = useState<{
    visible: boolean
    title: string
    viewUrl?: string
    downloadUrl?: string
  }>({
    visible: false,
    title: '',
    viewUrl: undefined,
    downloadUrl: undefined,
  })

  // PMS Job creation
  const [createPmsJobOpen, setCreatePmsJobOpen] = useState(false)
  const [vessels, setVessels] = useState<Vessel[]>([])
  const [companies, setCompanies] = useState<CompanyGroup[]>([])
  const [subcompanies, setSubcompanies] = useState<Subcompany[]>([])
  const [loadingLookups, setLoadingLookups] = useState(false)

  // Linked PMS Jobs
  const [linkedJobs, setLinkedJobs] = useState<PmsJobRecord[]>([])
  const [loadingJobs, setLoadingJobs] = useState(false)
  const [jobDetailOpen, setJobDetailOpen] = useState(false)
  const [selectedJob, setSelectedJob] = useState<PmsJobRecord | null>(null)

  useEffect(() => {
    if (visible && record?.id) {
      loadLinkedJobs()
      if (createPmsJobOpen) {
        loadLookups()
      }
    }
  }, [visible, record?.id, createPmsJobOpen])

  const loadLookups = async () => {
    setLoadingLookups(true)
    try {
      const [groupsRaw, adminsRaw, vesselsData] = await Promise.all([
        getCompanyAdminList?.().catch(() => []),
        getCompanyList?.().catch(() => []),
        getVesselList().catch(() => [] as Vessel[]),
      ])

      const groups: CompanyGroup[] = Array.isArray(groupsRaw)
        ? groupsRaw.map((g: any) => ({
            id: Number(g.id),
            name: g.name ?? g.companyGroupAdminName ?? `Group #${g.id}`,
          }))
        : []

      const admins: Subcompany[] = Array.isArray(adminsRaw)
        ? adminsRaw.map((a: any) => ({
            id: Number(a.id),
            name: a.name ?? a.companyAdminName ?? `Company #${a.id}`,
            companyId: Number(
              a.cgaid?.id ??
              a.cga?.id ??
              a.companyGroupAdminId ??
              a.companyGroupId
            ),
          }))
        : []

      setCompanies(groups)
      setSubcompanies(admins)
      setVessels(vesselsData)
    } catch (error) {
      console.error('Error loading lookups:', error)
    } finally {
      setLoadingLookups(false)
    }
  }

  const loadLinkedJobs = async () => {
    if (!record?.id) return
    setLoadingJobs(true)
    try {
      const jobs = await searchPmsJobs(undefined, undefined, undefined, undefined, record.id)
      setLinkedJobs(jobs)
    } catch (error) {
      console.error('Error loading linked PMS jobs:', error)
    } finally {
      setLoadingJobs(false)
    }
  }

  const handleCreatePmsJob = () => {
    setCreatePmsJobOpen(true)
  }

  const handleJobCreated = () => {
    setCreatePmsJobOpen(false)
    loadLinkedJobs()
    toast.success('PMS Job created successfully', { position: 'top-center' })
  }

  const handleViewJob = (job: PmsJobRecord) => {
    setSelectedJob(job)
    setJobDetailOpen(true)
  }

    if (!visible || !record) return null

    const subcomponentNames =
      record.subComponentNames?.filter((name) => name != null && String(name).trim().length > 0) || []

    const jobGroups = (() => {
      const groups = new Map<string, string[]>()
      const ids = record.jobPlanLineIds && record.jobPlanLineIds.length > 0
        ? record.jobPlanLineIds
        : record.jobPlanLineId
        ? [record.jobPlanLineId]
        : []
      const codes = record.jobPlanLineJobCodes || []
      const tasks = record.jobPlanLineTaskDescriptions || []
      const subNames = record.jobPlanLineSubComponentNames || []

      const push = (group: string, label: string) => {
        if (!label || !label.trim()) return
        const list = groups.get(group) ?? []
        if (!list.includes(label)) {
          list.push(label)
          groups.set(group, list)
        }
      }

      if (ids.length > 0) {
        ids.forEach((id, idx) => {
          const code = codes[idx]
          const task = tasks[idx]
          const label = code && task ? `${code} - ${task}` : code || task || (id ? `Plan #${id}` : '')
          const groupName = subNames[idx] || record.subComponentName || 'Unassigned Subcomponent'
          push(groupName, label)
        })
      } else if (record.jobPlanLineJobCode || record.jobPlanLineTaskDescription || record.jobPlanLineId) {
        const label = record.jobPlanLineJobCode && record.jobPlanLineTaskDescription
          ? `${record.jobPlanLineJobCode} - ${record.jobPlanLineTaskDescription}`
          : record.jobPlanLineJobCode || record.jobPlanLineTaskDescription || (record.jobPlanLineId ? `Plan #${record.jobPlanLineId}` : '')
        const groupName = record.subComponentName || 'Unassigned Subcomponent'
        push(groupName, label)
      }

      return Array.from(groups.entries()).map(([name, jobs]) => ({ name, jobs }))
    })()

    const subcomponentBadgeLabels =
      subcomponentNames.length > 0
        ? subcomponentNames
        : record.subComponentName
        ? [record.subComponentName]
        : []

  const lines: {label: string; value: string}[] = [
    {label: 'Vessel', value: record.vesselName || '-'},
    {label: 'Defect Number', value: record.defectNumber || '-'},
    {label: 'Category', value: niceEnum(record.category)},
    {
      label: 'TPI Sub Category',
      value: record.tpiSubCategory ? niceEnum(record.tpiSubCategory) : '-',
    },
    {label: 'DAC Code', value: record.dacCode != null ? String(record.dacCode) : '-'},
    {label: 'DAC Action Name', value: record.dacActionName || '-'},
    {
      label: 'SMS Option',
      value: record.smsCode ? niceEnum(record.smsCode) : '-',
    },
    {label: 'Date of Observing', value: record.dateObserved || '-'},
    {label: 'Date Defect Raised On', value: record.dateDefect || '-'},
    {
      label: 'Applicable Requisition Number',
      value: record.applicableRequisitionNumber || '-',
    },
    {
      label: 'Department',
      value: record.department ? niceEnum(record.department) : '-',
    },
  ]

  const linkedRequisition = record.linkedRequisition


  const showActionsColumn = origin === 'defect'

  const wrapStyle = {
    ...BASE_WRAP_STYLE,
    zIndex: wrapZIndex ?? 1050,
  }

  return (
    <div style={wrapStyle} tabIndex={-1}>
      {/* Custom wide modal window – consistent with Add/Edit Defect modals */}
      <div
        className='bg-white text-dark rounded shadow-lg d-flex flex-column overflow-hidden'
        style={{
          width: '75vw',
          height: '75vh',
          maxWidth: '1400px',
          maxHeight: '900px',
        }}
      >
        {/* Header */}
        <div className='d-flex align-items-center justify-content-between border-bottom px-4 py-3 flex-shrink-0'>
          <h5 className='modal-title text-dark m-6'>View Defect</h5>
          <button type='button' className='btn-close m-6' onClick={onClose} />
        </div>

        {/* Body with inner scroll */}
        <div className='flex-grow-1 overflow-auto px-4 py-3 min-h-0' style={{ margin: '0 1.5rem 1rem' }}>
          {record.displayTitle && (
            <div className='alert alert-light-primary background-primary bg-opacity-10 border-primary border-start-5 mb-5 p-4'>
              <strong>{record.displayTitle}</strong>
            </div>
          )}

            <div className='row g-3'>
              {linkedRequisition && (
                <div className='col-12'>
                  <div className='border rounded p-3 bg-light-primary'>
                    <div className='fw-bold text-dark mb-1'>Linked Requisition</div>
                    <button
                      type='button'
                      className='btn btn-link p-0 text-primary fw-semibold'
                      onClick={() => navigate(linkedRequisitionPath(linkedRequisition.id))}
                    >
                      {linkedRequisition.requisitionNumber || `Requisition #${linkedRequisition.id}`}
                    </button>
                    <span className='ms-2 text-dark'>{linkedRequisition.title || '-'}</span>
                    <div className='small text-muted mt-1'>
                      Status: {niceEnum(linkedRequisition.status)} · Raised: {linkedRequisition.raisedAt ? linkedRequisition.raisedAt.slice(0, 10) : '-'}
                    </div>
                  </div>
                </div>
              )}
            {lines.map((l, idx) => (
              <div className='col-md-6' key={idx}>
                <div className='fw-semibold text-muted fs-7 mb-1'>{l.label}</div>
                <div className='fw-bold'>{l.value}</div>
              </div>
            ))}


<div className='row mb-3'>
  <label className='col-sm-4 fw-semibold text-muted'>
    Linked To
  </label>
  <div className='col-sm-8'>
    {record.inspectionId ? (
      <>
        Inspection –{' '}
        <span className='fw-semibold'>
          {record.inspectionDisplayName ||
            record.displayTitle ||
            `#${record.inspectionId}`}
        </span>
      </>
    ) : record.auditId ? (
      <>
        Audit –{' '}
        <span className='fw-semibold'>
          {record.auditDisplayName ||
            record.displayTitle ||
            `#${record.auditId}`}
        </span>
      </>
    ) : (
      <span className='text-muted'>-</span>
    )}
  </div>
</div>

{/* Equipment/Subcomponent Links */}
{(record.equipmentId || subcomponentBadgeLabels.length > 0 || jobGroups.length > 0) && (
  <div className='row mb-3'>
    <label className='col-sm-4 fw-semibold text-muted'>
      Machinery Links
    </label>
    <div className='col-sm-8 d-flex flex-column gap-3'>
      {record.equipmentName && (
        <div>
          <div className='text-muted fs-8 mb-1'>Equipment</div>
          <span className='badge badge-light-primary text-dark border border-primary'>{record.equipmentName}</span>
        </div>
      )}
      {subcomponentBadgeLabels.length > 0 && (
        <div>
          <div className='text-muted fs-8 mb-1'>Subcomponents</div>
          <div className='d-flex flex-wrap gap-2'>
            {subcomponentBadgeLabels.map((name, idx) => (
              <span key={`${name}-${idx}`} className='badge badge-light-info text-dark border border-info'>
                {name}
              </span>
            ))}
          </div>
        </div>
      )}
      {jobGroups.length > 0 && (
        <div>
          <div className='text-muted fs-8 mb-1'>Jobs (Grouped by Subcomponent)</div>
          <div className='d-flex flex-column gap-3'>
            {jobGroups.map((group) => (
              <div key={group.name} className='bg-white rounded p-2 border border-dashed border-gray-200'>
                <div className='mb-2'>
                  <span className='badge badge-light-info text-dark border border-info'>{group.name}</span>
                </div>
                <div className='d-flex flex-wrap gap-2'>
                  {group.jobs.map((label, idx) => (
                    <span key={`${group.name}-${idx}`} className='badge badge-light-success text-dark border border-success'>
                      {label}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {!record.equipmentName && subcomponentBadgeLabels.length === 0 && jobGroups.length === 0 && (
        <span className='text-muted'>N/A</span>
      )}
    </div>
  </div>
)}

{/* Job Request Status */}
{record.jobRequestStatus && record.jobRequestStatus !== 'NONE' && (
  <div className='row mb-3'>
    <label className='col-sm-4 fw-semibold text-muted'>
      Job Request Status
    </label>
    <div className='col-sm-8'>
      <span className={`badge ${
        record.jobRequestStatus === 'PENDING' ? 'badge-light-warning' :
        record.jobRequestStatus === 'ACCEPTED' ? 'badge-light-success' :
        record.jobRequestStatus === 'REJECTED' ? 'badge-light-danger' :
        'badge-light-secondary'
      }`}>
        {record.jobRequestStatus === 'PENDING' ? 'Pending' :
         record.jobRequestStatus === 'ACCEPTED' ? 'Accepted' :
         record.jobRequestStatus === 'REJECTED' ? 'Rejected' :
         record.jobRequestStatus}
      </span>
      {record.jobRequestCreatedAt && (
        <div className='text-muted fs-7 mt-1'>
          Created: {new Date(record.jobRequestCreatedAt).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>
      )}
      {record.jobRequestProcessedAt && (
        <div className='text-muted fs-7 mt-1'>
          Processed: {new Date(record.jobRequestProcessedAt).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
          {record.jobRequestProcessedByName && (
            <> by {record.jobRequestProcessedByName}</>
          )}
        </div>
      )}
    </div>
  </div>
)}

                        {/* Description */}
            <div className='col-12'>
              <div className='fw-semibold text-muted fs-7 mb-1'>Description</div>
              <div
                className='border rounded p-2 bg-light-subtle'
                style={{minHeight: '60px'}}
              >
                {record.description || '-'}
              </div>
            </div>

            {/* Corrective Action */}
            <div className='col-12'>
              <div className='fw-semibold text-muted fs-7 mb-1'>Corrective Action</div>
              <div
                className='border rounded p-2 bg-light-subtle'
                style={{minHeight: '60px'}}
              >
                {record.correctiveAction || '-'}
              </div>
            </div>

            {/* Preventive Action */}
            <div className='col-12'>
              <div className='fw-semibold text-muted fs-7 mb-1'>Preventive Action</div>
              <div
                className='border rounded p-2 bg-light-subtle'
                style={{minHeight: '60px'}}
              >
                {record.preventiveAction || '-'}
              </div>
            </div>

            {/* Remarks */}
            <div className='col-12'>
              <div className='fw-semibold text-muted fs-7 mb-1'>Remarks</div>
              <StructuredRemarksView text={record.remarks} emptyMessage='-' />
            </div>

            {/* Shore Remarks */}
            {record.shoreRemarks && (
              <div className='col-12'>
                <div className='fw-semibold text-primary fs-7 mb-1'>Shore Remarks</div>
                <StructuredRemarksView text={record.shoreRemarks} emptyMessage='-' />
              </div>
            )}

            {/* Rejection Remark */}
            {record.rejectionRemark && (
              <div className='col-12'>
                <div className='fw-semibold text-danger fs-7 mb-1'>Rejection Reason</div>
                <StructuredRemarksView text={record.rejectionRemark} emptyMessage='-' />
              </div>
            )}

            {/* Closure Remark */}
            <div className='col-12'>
              <div className='fw-semibold text-muted fs-7 mb-1'>Closure Remark</div>
              <StructuredRemarksView text={record.closureRemark} emptyMessage='-' />
            </div>

            {/* Office Review */}
            <div className='col-12'>
              <div className='fw-semibold text-muted fs-7 mb-1'>Office Remarks</div>
              <StructuredRemarksView text={record.officeRemarks} emptyMessage='-' />
              {record.officeReviewedAt && <div className='text-muted small mt-1'>
                Office reviewed{record.officeReviewedByName ? ` by ${record.officeReviewedByName}` : ''}
                {' on '}{new Date(record.officeReviewedAt).toLocaleString()}
              </div>}
            </div>
{/*
            <div className='col-md-6'>
              <div className='fw-semibold text-muted fs-7 mb-1'>Attachment</div>
              {record.attachmentPath && record.id ? (
                <div className='d-flex align-items-center flex-wrap gap-2'>
                  <button
                    type='button'
                    className='btn btn-light-primary btn-outline btn-sm d-inline-flex align-items-center'
                    onClick={() =>
                      setFileView({
                        visible: true,
                        title: 'Defect Attachment',
                        viewUrl: defectAttachmentViewUrl(record.id!),
                        downloadUrl: defectAttachmentDownloadUrl(record.id!),
                      })
                    }
                  >
                    View
                  </button>
                </div>
              ) : (
                <div>-</div>
              )}
            </div> */}

            {/* Attachments (multi-file) */}
<div className='col-12'>
  <div className='fw-semibold text-muted fs-7 mb-1'>Attachments</div>
  {record.id ? (
    <button
      type='button'
      className='btn btn-light-primary btn-sm'
      onClick={() => onOpenAttachments && onOpenAttachments(record)}
    >
      Open Attachment List
    </button>
  ) : (
    <span className='text-muted'>-</span>
  )}
</div>


            <div className='col-md-6'>
              <div className='fw-semibold text-muted fs-7 mb-1'>Closure Evidence</div>
              {record.closureEvidencePath && record.id ? (
                <div className='d-flex align-items-center flex-wrap gap-2'>
                  <button
                    type='button'
                    className='btn btn-light-primary btn-outline btn-sm d-inline-flex align-items-center'
                    onClick={() =>
                      setFileView({
                        visible: true,
                        title: 'Defect Closure Evidence',
                        viewUrl: defectClosureViewUrl(record.id!),
                        downloadUrl: defectClosureDownloadUrl(record.id!),
                      })
                    }
                  >
                    View
                  </button>
                </div>
              ) : (
                <div>-</div>
              )}
            </div>

            {/* Linked PMS Jobs - full width row, inside scrollable body */}
            {record.status === 'SUBMIT' && (
              <div className='col-12 mt-4 mb-2'>
                <h6 className='fw-bold mb-3'>Linked PMS Jobs</h6>
                <div className='d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2'>
                  <span className='text-muted fs-7'>Jobs created from this defect</span>
                  <button
                    type='button'
                    className='btn btn-sm btn-primary'
                    onClick={handleCreatePmsJob}
                  >
                    Create PMS Job
                  </button>
                </div>
                {loadingJobs ? (
                  <div className='text-center py-3'>
                    <div className='spinner-border spinner-border-sm'></div>
                  </div>
                ) : linkedJobs.length === 0 ? (
                  <div className='border rounded p-3 bg-light'>
                    <p className='text-muted text-center mb-0'>No PMS jobs linked to this defect.</p>
                    {record.jobRequestStatus === 'PENDING' && (
                      <p className='text-center mb-0 mt-2'>
                        <span className='badge badge-light-warning'>Job request pending approval</span>
                        <span className='ms-2 text-muted fs-7'>Accept or reject on the PMS Jobs page.</span>
                      </p>
                    )}
                  </div>
                ) : (
                  <div className='table-responsive'>
                    <table className='table table-bordered table-sm mb-0'>
                      <thead>
                        <tr>
                          <th>Job Code</th>
                          <th>Title</th>
                          <th>State</th>
                          <th>Due Date</th>
                          <th>Completion Date</th>
                          {showActionsColumn && <th>Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {linkedJobs.map((job) => (
                          <tr key={job.id}>
                            <td>{job.jobCode || `Job #${job.id}`}</td>
                            <td>{job.title || '-'}</td>
                            <td>
                              <span className={`badge ${
                                job.state === 'COMPLETED' || job.state === 'VERIFIED' ? 'badge-light-success' :
                                job.state === 'IN_PROGRESS' ? 'badge-light-warning' :
                                job.state === 'OVERDUE' ? 'badge-light-info' :
                                'badge-light-secondary'
                              }`}>
                                {job.state || '-'}
                              </span>
                            </td>
                            <td>{job.dueDate ? new Date(job.dueDate).toLocaleDateString() : '-'}</td>
                            <td>{job.actualCompletionDate ? new Date(job.actualCompletionDate).toLocaleDateString() : '-'}</td>
                            {showActionsColumn && (
                              <td>
                                <button
                                  className='btn btn-sm btn-light-primary'
                                  onClick={() => handleViewJob(job)}
                                >
                                  View
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer - always visible at bottom of modal */}
        <div className='border-top d-flex justify-content-end gap-2 px-4 py-3 flex-shrink-0'>
          <button
            type='button'
            className='btn btn-light btn-sm'
            onClick={onClose}
          >
            Close
          </button>
        </div>

      {/* File viewer for attachment/closure evidence */}
      <FileViewerModal
        visible={fileView.visible}
        onClose={() =>
          setFileView(prev => ({...prev, visible: false}))
        }
        title={fileView.title}
        fileName={fileView.title}
        viewUrl={fileView.viewUrl}
        downloadUrl={fileView.downloadUrl}
      />

      {/* Create PMS Job Modal */}
      {createPmsJobOpen && record && (
        <CreateUnplannedJobModal
          visible={createPmsJobOpen}
          onClose={() => setCreatePmsJobOpen(false)}
          onSuccess={handleJobCreated}
          vessels={vessels}
          companies={companies}
          subcompanies={subcompanies}
          showCompanyFilters={true}
          isCrew={false}
          initialDefectId={record.id || undefined}
          initialVesselId={record.vesselId || undefined}
          initialEquipmentId={record.equipmentId || undefined}
          initialSubComponentId={
            record.subComponentIds && record.subComponentIds.length === 1
              ? record.subComponentIds[0]
              : record.subComponentId || undefined
          }
        />
      )}
      {jobDetailOpen && selectedJob && (
        <PmsJobDetailModal
          visible={jobDetailOpen}
          onClose={() => {
            setJobDetailOpen(false)
            setSelectedJob(null)
          }}
          record={selectedJob}
          onRefresh={() => {
            loadLinkedJobs()
            setJobDetailOpen(false)
            setSelectedJob(null)
          }}
          hideDefectViewButton={true}
        />
      )}
    </div>
    </div>
  )
}
